import React, {useCallback, useEffect, useState} from "react";

import { useNavigate } from "react-router-dom";
import PopupDownNewSequenceLoadingStyle from './PopupDownNewSequenceLoading.module.css';
import { ReactComponent as LoaderIcon } from '../../assets/images/loader.svg';
import api from "../../services/api";
import {usePopup} from "../../services/PopupContext";
import {useAppContext} from "../../provider/appProvider";

// Libellés des étapes remontées par la centrale ou le NVR. Le back n'envoie que des
// identifiants structurels (step/stepIndex/current/total) : toute la formulation vit ici.
//
// Les utilisateurs non-staff reçoivent une progression volontairement réduite par
// l'API : seuls 'queued' / 'running' / 'failed' / 'done' leur parviennent, sans
// compteur. Le rendu s'adapte donc au niveau de détail reçu, il ne le décide pas.
const STEP_LABELS = {
  queued: 'Demande envoyée à la centrale',
  running: 'Génération en cours',
  segments: 'Recherche des enregistrements',
  extracting: 'Extraction des images',
  encoding: 'Assemblage de la vidéo',
  waiting: 'Enregistrement en cours',
  uploading: 'Transfert de la vidéo',
};

const FAILURE_LABELS = {
  NO_SEGMENTS: "Aucun enregistrement disponible sur cette période",
  NO_SCREENSHOTS: "Aucun enregistrement disponible sur cette période",
  NO_TARGET_SEGMENT: "Aucun enregistrement disponible à cet instant",
  ENCODING_FAILED: "L'assemblage de la vidéo a échoué",
  EMPTY_PLAYLIST: "L'assemblage de la vidéo a échoué",
};

const UNIT_LABELS = {
  frames: 'images',
  segments: 'segments',
};

const PopupDownNewSequenceLoading = (props) => {
  const { timelapseID, recordID, index } = props;
  const [isReady, setIsReady] = useState(false);
  const [progress, setProgress] = useState(null);
  const navigate = useNavigate();
  const { hidePopup } = usePopup()
  const { setTimelapses, setRecords } = useAppContext();

  // Une restitution temps réel est un record servi par le NVR, un timelapse est généré
  // par la route /timelapses : les deux se suivent, mais sur des routes différentes.
  const isRecord = !!recordID;
  const resourcePath = isRecord ? `/records/${recordID}` : `/timelapses/${timelapseID}`;

  const handleSeeSequence = () => {
    hidePopup(index);
    navigate(isRecord ? `/records/${recordID}` : `/timelapses/${timelapseID}`, {replace: true});
  }

  const addToList = (element, setList) => {
    setList((list) => list.some((item) => item.id === element.id) ? list : [...list, element]);
  };

  // La séquence est prête : on récupère l'objet complet une seule fois pour
  // l'injecter dans la liste, au lieu de le retélécharger à chaque tick.
  const loadFinishedSequence = useCallback(async () => {
    try {
      const response = await api.get(resourcePath);
      const element = isRecord ? response.data.record : response.data.timelapse;
      if (element) addToList(element, isRecord ? setRecords : setTimelapses);
    } catch (error) {
      console.log((error.response && error.response.data) || error.message || error.toString());
    }
  }, [resourcePath, isRecord, setRecords, setTimelapses]);

  const verifyProgress = useCallback(async () => {
    try {
      const response = await api.get(`${resourcePath}/progress`);
      const data = response.data;
      setProgress(data);

      if (data.ready) {
        await loadFinishedSequence();
        setIsReady(true);
      }
    } catch (error) {
      console.log((error.response && error.response.data) || error.message || error.toString());
    }
  }, [resourcePath, loadFinishedSequence]);

  const hasFailed = progress?.failed === true;

  useEffect(() => {
    if (isReady || hasFailed) return;

    const intervalId = setInterval(() => {
      verifyProgress();
    }, 1000);
    verifyProgress();

    return () => {
      clearInterval(intervalId);
    };
  }, [isReady, hasFailed, verifyProgress]);

  const renderProgress = () => {
    if (!progress) return null;

    const stepLabel = STEP_LABELS[progress.step] ?? 'Génération en cours';
    const showCounter = progress.current !== null
      && progress.total > 0
      && UNIT_LABELS[progress.unit];

    return (
      <div className={PopupDownNewSequenceLoadingStyle.progress}>
        <span className={PopupDownNewSequenceLoadingStyle.step}>
          {progress.stepIndex > 0 && `Étape ${progress.stepIndex}/${progress.stepCount} · `}
          {stepLabel}
        </span>

        <div className={PopupDownNewSequenceLoadingStyle.bar}>
          <div
            className={PopupDownNewSequenceLoadingStyle.barFill}
            style={{width: `${progress.percent}%`}}
          />
        </div>

        {showCounter && (
          <span className={PopupDownNewSequenceLoadingStyle.counter}>
            {progress.current} / {progress.total} {UNIT_LABELS[progress.unit]}
          </span>
        )}

        {progress.stale && (
          <span className={PopupDownNewSequenceLoadingStyle.warning}>
            La centrale ne répond plus depuis un moment…
          </span>
        )}
      </div>
    );
  };

  const title = hasFailed
    ? 'La génération a échoué'
    : (isReady ? 'Votre séquence est disponible' : 'Création de la séquence en cours');

  return (
    <div className={PopupDownNewSequenceLoadingStyle.container}>
      <span className={PopupDownNewSequenceLoadingStyle.title}>{title}</span>

      {hasFailed && (
        <>
          <span className={PopupDownNewSequenceLoadingStyle.error}>
            {FAILURE_LABELS[progress.reason] ?? 'Une erreur est survenue pendant la génération.'}
          </span>
          <input value={'Fermer'} type={"button"} className={PopupDownNewSequenceLoadingStyle.see}
                 onClick={() => hidePopup(index)}/>
        </>
      )}

      {isReady && !hasFailed &&
        <input value={'Aller à la séquence'} type={"button"} className={PopupDownNewSequenceLoadingStyle.see}
               onClick={handleSeeSequence}/>
      }

      {!isReady && !hasFailed &&
        <>
          <LoaderIcon className={'loader'} height={50} width={50}/>
          {renderProgress()}
        </>
      }
    </div>
  );
};

export default PopupDownNewSequenceLoading;
